import { api } from './client'
import type {
  ConfigResponse,
  ConfigUpdateRequest,
  DocumentsListResponse,
  ChunksListResponse,
  DocumentStatusResponse,
  UploadResponse,
  ExtractionTestRequest,
  ExtractionTestResponse,
  Conversation,
  ConversationListResponse,
  ConversationHistoryResponse,
  UserProfile,
  Document,
  DashboardStats,
  RecentConversation,
  TrendData,
  SystemSettings,
  LogEntry,
  HealthStatus,
  PaginatedResponse
} from './types'
import type { CompanyResponse, CompanyUpdateRequest } from '@/types/auth.types'

// 认证相关API (保持不变)
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    api.post<{ access_token: string; refresh_token: string }>('/auth/login', credentials),

  logout: () => api.post('/auth/logout'),

  refreshToken: (refreshToken: string) =>
    api.post<{ access_token: string }>('/auth/refresh', { refresh_token: refreshToken }),

  getProfile: () => api.get<UserProfile>('/auth/profile'),
}

// 配置管理API
export const configApi = {
  // 获取所有配置
  getConfig: () => api.get<ConfigResponse>('/config'),

  // 更新配置
  updateConfig: (request: ConfigUpdateRequest) => api.post('/config', request),
}

// 知识库管理API (文档管理)
export const managementApi = {
  // 获取文档列表
  getDocuments: (statusFilter?: string) =>
    api.get<DocumentsListResponse>('/management/documents', { params: { status_filter: statusFilter } }),

  // 获取文档chunks
  getDocumentChunks: (documentId: string) =>
    api.get<ChunksListResponse>(`/management/documents/${documentId}/chunks`),

  // 获取文档状态
  getDocumentStatus: (documentId: string) =>
    api.get<DocumentStatusResponse>(`/management/documents/${documentId}/status`),

  // 删除文档
  deleteDocument: (documentId: string) =>
    api.delete(`/management/documents/${documentId}`),

  // 获取文档详情
  getDocument: (documentId: string) =>
    api.get<Document>(`/management/documents/${documentId}`),
}

// 知识库上传和测试API
export const knowledgeApi = {
  // 上传文档
  uploadDocument: (file: File, onProgress?: (progress: number) => void) =>
    api.upload<UploadResponse>('/knowledge/upload', file, onProgress),

  // 知识提取测试
  extractionTest: (request: ExtractionTestRequest) =>
    api.post<ExtractionTestResponse>('/knowledge/extraction-test', request),
}

// 对话管理API (兼容端点)
export const conversationApi = {
  // 获取对话列表 (兼容端点)
  getConversations: () =>
    api.get<ConversationListResponse>('/message/conversations/compat'),

  // 获取对话历史 (兼容端点)
  getConversationHistory: (conversationId: string, limit?: number) =>
    api.get<ConversationHistoryResponse>(`/message/conversation/${conversationId}/history/compat`,
      { params: { limit } }),
}

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get<DashboardStats>('/dashboard/stats'),
  getRecentConversations: (limit = 10) =>
    api.get<RecentConversation[]>('/dashboard/recent-conversations', { params: { limit } }),
  getTrends: (days = 7) =>
    api.get<TrendData>('/dashboard/trends', { params: { days } }),
}

// 系统设置API (兼容旧接口，但端点可能需要调整)
export const settingsApi = {
  // 获取系统设置
  getSettings: () => api.get<SystemSettings>('/settings'),

  // 更新系统设置
  updateSettings: (data: Partial<SystemSettings>) =>
    api.put<SystemSettings>('/settings', data),

  // 获取日志
  getLogs: (params?: { level?: string; start_date?: string; end_date?: string; page?: number; limit?: number }) =>
    api.get<PaginatedResponse<LogEntry>>('/settings/logs', { params }),

  // 系统健康检查
  healthCheck: () => api.get<HealthStatus>('/settings/health'),
}

// 公司管理API
export const companiesApi = {
  // 获取当前公司信息
  getCurrentCompany: () => api.get<CompanyResponse>('/companies/me'),

  // 更新当前公司信息
  updateCurrentCompany: (data: CompanyUpdateRequest) =>
    api.put<CompanyResponse>('/companies/me', data),
}